/**
 * GGPO-style rollback session (ARCHITECTURE §6). Generic over `RollbackGame<S, InputFrame, E, C>`.
 *
 * Tick model: "state T" is the state after T steps (tickOf = T). "Input of tick t" advances state t → t+1.
 *
 *  - Local inputs: the sample passed to `tick()` while the session is at tick L is the input of tick
 *    L + inputDelay. Ticks [0, delay) are neutral. Input ticks are strictly contiguous: a delay increase repeats
 *    the last input, a decrease drops samples (packet.ts rules). Every local input is sent, redundantly, from
 *    the peer's ack up to the newest, in every INPUT packet (one per tick + pump keepalives).
 *  - Remote inputs arrive in any order (unreliable/unordered transport), are stored by tick in a ring, and are
 *    "contiguous up to" `remoteEnd` (acked back). Unknown remote inputs are predicted as the last contiguous
 *    remote input (repeat last input). The input actually used for every simulated tick is remembered; an
 *    arriving input that differs from it schedules a rollback to that tick.
 *  - Rollback: at the start of `tick()`, restore the snapshot of the first incorrect tick from a preallocated ring
 *    (`cloneInto`, no allocation) and resimulate up to the present with events ON (de-duplicated by eventKey).
 *  - Prediction barrier: never simulate more than `maxRollback` ticks past the last contiguous remote input, so
 *    every rollback is ≤ maxRollback ticks. When the barrier is hit `tick()` returns false ('stalled').
 *  - Time sync: the GGPO frame advantage (localAdv − remoteAdv) / 2, smoothed; when we are ≥ 1 tick ahead we
 *    insert one wait frame, at most once per `timeSyncWaitEvery` ticks.
 *  - Confirmation: tick t is confirmed once both inputs are final and it has been simulated with them. Confirmed
 *    ticks fire `onConfirmed` in order, exactly once. Every `hashInterval` confirmed ticks the state hash is
 *    sent (HASH, re-sent by the pump) and compared with the peer's; a mismatch triggers HASH_DETAIL exchange,
 *    a JSON dump of the kept snapshot, `onDesyncReport` and `onDesync`.
 *  - A packet pump on the injected NetScheduler keeps HELLO/INPUT acks/PING/HASH flowing when `tick()` is not
 *    being called (hidden tab) and detects timeouts.
 *  - Game over: the final inputs are flushed the moment the game-over tick is confirmed, and `dispose()` lingers
 *    (silently: no handlers, no events) until the peer acked every input of the match or `disconnectTimeoutMs`
 *    passed, so a peer that loses our last INPUT packet still reaches the same game-over tick.
 */
import {
  Fnv1a,
  HASH_PART_COUNT,
  HASH_PART_NAMES,
  INPUT_HEADER_BYTES,
  MAX_INPUTS_PER_PACKET,
  NO_TICK,
  PROTOCOL_VERSION,
  PacketType,
  QuitReason,
  SIM_CONTENT_HASH,
  TICK_MS,
  decodePacket,
  encodePacket,
  formatHash,
  sanitizeInput,
  type DriverStatus,
  type EventSink,
  type GamePacket,
  type HashDetailPacket,
  type HelloPacket,
  type InputFrame,
  type InputPacket,
  type NetStats,
  type RollbackGame,
  type Transport,
} from '@hf/shared';
import { createDefaultScheduler } from './scheduler';
import {
  NETCODE_DEFAULTS,
  TAP_CARRY_BITS,
  type DesyncReport,
  type DisconnectReason,
  type NetLogger,
  type NetScheduler,
  type RollbackSession,
  type RollbackSessionOptions,
  type SessionMetrics,
  type TickRefusal,
} from './types';

/** Input ring capacity (ticks). Must exceed every in-flight window by a wide margin. Power of two. */
const INPUT_RING = 512;
const INPUT_MASK_RING = INPUT_RING - 1;
/** Remote input ticks further than this past our newest local input are implausible (stale session/garbage). */
const MAX_REMOTE_LEAD = 128;
/** Frame-advantage smoothing window (samples, one per tick() call). */
const ADV_WINDOW = 32;
/** Insert a wait frame when the smoothed advantage reaches this many ticks. */
const WAIT_THRESHOLD = 1;
/** Remote hash slots kept for comparison. */
const REMOTE_HASH_SLOTS = 16;
/** Events kept for draining if the client never drains (should not happen). */
const MAX_PENDING_EVENTS = 4096;
/** Consecutive stalled ticks before the status reports 'stalled' (100 ms). */
const STALL_STATUS_TICKS = 6;
/** How many copies of a QUIT to send (unreliable channel). */
const QUIT_COPIES = 3;
/**
 * Auto delay dead band (ticks of wanted delay x): raise when x > delay + UP, lower when x < delay − DOWN. The band
 * is 1.5 ticks wide, so after a raise at x = d + 0.25 a lowering needs x < d − 0.25: 0.5 tick (≈ 17 ms of RTT) of
 * hysteresis on top of the hold times. The first pick, ceil(x − UP), lands inside the band.
 */
const AUTO_DELAY_BAND_UP = 0.25;
const AUTO_DELAY_BAND_DOWN = 1.25;
/** Confirmed-only events held for unconfirmed ticks (bounded by the prediction window; defensive cap). */
const MAX_HELD_EVENTS = 4096;

const TERMINAL: ReadonlySet<DriverStatus> = new Set<DriverStatus>(['desynced', 'disconnected', 'ended']);

const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);

const defaultLogger: NetLogger = {
  info: (...a) => console.info(...a),
  warn: (...a) => console.warn(...a),
  error: (...a) => console.error(...a),
};

/**
 * The HELLO `matchKey` a session uses: FNV-1a of the matchId folded with the hash of the initial state
 * (`game.hash(game.createState(config))`), so peers must agree on both the match and its config.
 */
export const matchKeyFor = (matchId: string, initialStateHash: number): number =>
  new Fnv1a()
    .str(matchId)
    .u32(initialStateHash >>> 0)
    .digest();

const defaultEventKey = (e: unknown, tick: number): string => {
  if (e !== null && typeof e === 'object') {
    const o = e as { type?: unknown; tick?: unknown; actor?: unknown };
    const t = typeof o.tick === 'number' ? o.tick : tick;
    return `${t}:${String(o.type)}:${String(o.actor ?? '')}`;
  }
  return `${tick}:${String(e)}`;
};

interface HashSnap<S> {
  tick: number;
  hash: number;
  state: S;
}

interface DesyncState {
  tick: number;
  localHash: number;
  remoteHash: number | null;
  localParts: number[] | null;
  remoteParts: number[] | null;
  snapshotJson: string | null;
  deadline: number;
  finalized: boolean;
}

export class RollbackSessionImpl<S, E, C> implements RollbackSession<S, E> {
  readonly timePolicy = 'catch_up' as const;
  readonly localPlayer: 0 | 1;
  private readonly remotePlayer: 0 | 1;

  private readonly game: RollbackGame<S, InputFrame, E, C>;
  private readonly transport: Transport;
  private readonly scheduler: NetScheduler;
  private readonly logger: NetLogger;
  private readonly opts: RollbackSessionOptions<S, E, C>;
  private readonly matchKey: number;
  private readonly contentHash: number;

  private readonly maxRollback: number;
  private readonly hashInterval: number;
  private readonly minDelay: number;
  private readonly maxDelay: number;
  private readonly minAutoDelay: number;
  private readonly maxRepay: number;
  private readonly hashResendCount: number;
  private readonly waitEvery: number;
  private readonly pingIntervalMs: number;
  private readonly pumpIntervalMs: number;
  private readonly disconnectTimeoutMs: number;
  private readonly handshakeTimeoutMs: number;
  private readonly detailTimeoutMs: number;
  private autoDelay: boolean;
  private delay: number;

  // ── States ────────────────────────────────────────────────────────────────────────────────────────
  private readonly cur: S;
  /** Snapshot ring: ring[t % N] holds state t (before stepping tick t). */
  private readonly ring: S[];
  private readonly ringTick: Float64Array;
  private readonly initialPrev: S;
  private readonly base: number;
  private localTick: number;

  // ── Inputs ────────────────────────────────────────────────────────────────────────────────────────
  private readonly localIn = new Uint8Array(INPUT_RING);
  private localEnd: number;
  private lastLocal: InputFrame;
  private readonly neutral: InputFrame;
  private readonly remoteIn = new Uint8Array(INPUT_RING);
  private readonly remoteTicks = new Float64Array(INPUT_RING).fill(-1);
  private readonly usedRemote = new Uint8Array(INPUT_RING);
  private remoteEnd: number;
  /** The peer has every local input < this (from its acks). */
  private ackedEnd: number;
  private firstIncorrect = -1;
  private confirmedEnd: number;
  /** Confirmed tick whose state `isGameOver` accepted (−1 = still playing). */
  private gameOverTick = -1;
  private readonly stepInputs: InputFrame[];
  private readonly confirmedInputs: InputFrame[];

  // ── Packets ───────────────────────────────────────────────────────────────────────────────────────
  private readonly inBuf = new Uint8Array(INPUT_HEADER_BYTES + MAX_INPUTS_PER_PACKET);
  private readonly inView = new DataView(this.inBuf.buffer);
  private readonly inViews: Uint8Array[] = [];
  /** Reusable buffers for the small fixed-size packets (transports copy or send synchronously). */
  private readonly pingBuf = new Uint8Array(13);
  private readonly pingView = new DataView(this.pingBuf.buffer);
  private readonly hashBuf = new Uint8Array(9);
  private readonly hashView = new DataView(this.hashBuf.buffer);
  private helloBuf: Uint8Array = new Uint8Array(0);
  private lastInputSentAt = -Infinity;
  private lastPingAt = -Infinity;
  private pingId = 0;
  /** Last HELLO sent after our handshake (answers and unprompted offers; rate-limited so peers never echo). */
  private lastHelloAnswerAt = -Infinity;
  /** The peer has sent us INPUT, i.e. it completed its side of the handshake. */
  private peerReady = false;

  // ── Connection ────────────────────────────────────────────────────────────────────────────────────
  private _status: DriverStatus = 'waiting';
  private _disconnectReason: DisconnectReason | null = null;
  private started = false;
  private startedAt = 0;
  private helloOk = false;
  private lastRecvAt = 0;
  private disposed = false;
  /** Fully torn down (pump cancelled, transport unsubscribed). `disposed && !closed` = lingering after game over. */
  private closed = false;
  private lingerUntil = 0;
  private _lastRefusal: TickRefusal | null = 'waiting';
  private cancelPump: (() => void) | null = null;
  private readonly unsubs: (() => void)[] = [];
  /** Handler lists are copy-on-write arrays: iterating them in the hot path allocates nothing. */
  private statusHandlers: readonly ((s: DriverStatus) => void)[] = [];
  private confirmedHandlers: readonly ((tick: number, inputs: readonly InputFrame[]) => void)[] = [];
  private consecutiveStalls = 0;
  /** Tap buttons sampled during refused ticks, OR-ed into the next accepted sample. */
  private tapCarry = 0;

  // ── Time sync ─────────────────────────────────────────────────────────────────────────────────────
  private haveRemoteTick = false;
  private remoteSenderTick = 0;
  private remoteAdv = 0;
  private remoteDelay = 0;
  private readonly advSamples = new Float64Array(ADV_WINDOW);
  private advCount = 0;
  private advIdx = 0;
  private advSum = 0;
  private ticksSinceWait = 0;
  private lastAutoDelayAt = -Infinity;
  private autoDelayInitialized = false;
  /** Since when the wanted delay has been above (+1) / below (−1) the dead band (−Infinity = not). */
  private autoUpSince = -Infinity;
  private autoDownSince = -Infinity;
  /** Recent RTT samples (ring) + sort scratch for the percentile. */
  private readonly rttRing: Float64Array;
  private readonly rttScratch: Float64Array;
  private rttRingCount = 0;
  private rttRingIdx = 0;
  private rttEst = 0;
  /** Barrier refusals not yet repaid, and the tick until which they may be repaid. */
  private owedTicks = 0;
  private owedUntilTick = 0;

  // ── Hashes / desync ───────────────────────────────────────────────────────────────────────────────
  private readonly snaps: HashSnap<S>[];
  private readonly remoteHashTick = new Float64Array(REMOTE_HASH_SLOTS).fill(-1);
  private readonly remoteHashVal = new Uint32Array(REMOTE_HASH_SLOTS);
  private lastLocalHashTick = -1;
  private lastLocalHash = 0;
  /** Pump re-sends left for the newest local hash. */
  private hashResendsLeft = 0;
  private desync: DesyncState | null = null;
  private _desyncReport: DesyncReport | null = null;
  private readonly partsScratch = new Uint32Array(HASH_PART_COUNT);

  // ── Events ────────────────────────────────────────────────────────────────────────────────────────
  private readonly pending: E[] = [];
  private readonly delivered = new Set<string>();
  private readonly deliveredLog: { tick: number; key: string }[] = [];
  private steppingTick = 0;
  private readonly keyOf: (e: E, tick: number) => string;
  private readonly sink: EventSink<E>;
  private readonly confirmedOnly: ((e: E) => boolean) | null;
  /** Confirmed-only events of unconfirmed ticks, in tick order from `heldHead`. */
  private readonly heldEv: E[] = [];
  private readonly heldTick: number[] = [];
  private heldHead = 0;

  // ── Metrics ───────────────────────────────────────────────────────────────────────────────────────
  private readonly m = {
    rollbacks: 0,
    resimulatedTicks: 0,
    simulatedTicks: 0,
    lastRollbackDepth: 0,
    maxRollbackDepth: 0,
    stallTicks: 0,
    waitFrames: 0,
    repaidTicks: 0,
    delayChanges: 0,
    repeatedInputs: 0,
    skippedInputs: 0,
    hashesSent: 0,
    hashResends: 0,
    hashesCompared: 0,
    hashesMatched: 0,
    lastVerifiedTick: -1,
    packetsMalformed: 0,
    packetsRejected: 0,
    duplicateInputs: 0,
    eventsDelivered: 0,
    eventsDeduplicated: 0,
  };
  private readonly net: NetStats;

  constructor(opts: RollbackSessionOptions<S, E, C>) {
    const d = NETCODE_DEFAULTS;
    this.opts = opts;
    this.game = opts.game;
    this.transport = opts.transport;
    this.localPlayer = opts.localPlayer;
    this.remotePlayer = opts.localPlayer === 0 ? 1 : 0;
    if (this.game.playerCount !== 2) throw new RangeError('RollbackSession supports exactly 2 players');
    this.scheduler = opts.scheduler ?? createDefaultScheduler();
    this.logger = opts.logger ?? defaultLogger;
    this.contentHash = (opts.contentHash ?? SIM_CONTENT_HASH) >>> 0;

    this.maxRollback = Math.max(0, Math.floor(opts.maxRollback ?? d.maxRollback));
    this.hashInterval = Math.max(1, Math.floor(opts.hashInterval ?? d.hashInterval));
    this.minDelay = clamp(Math.floor(opts.minInputDelay ?? d.minInputDelay), 0, d.maxInputDelay);
    this.maxDelay = clamp(Math.floor(opts.maxInputDelay ?? d.maxInputDelay), this.minDelay, d.maxInputDelay);
    this.delay = clamp(Math.floor(opts.inputDelay ?? d.inputDelay), this.minDelay, this.maxDelay);
    this.autoDelay = opts.autoInputDelay ?? d.autoInputDelay;
    this.minAutoDelay = clamp(
      Math.floor(opts.minAutoInputDelay ?? d.minAutoInputDelay),
      this.minDelay,
      this.maxDelay,
    );
    this.maxRepay = Math.max(0, Math.floor(opts.maxStallRepayTicks ?? d.maxStallRepayTicks));
    this.hashResendCount = this.transport.kind === 'relay' ? 0 : d.hashResends;
    this.rttRing = new Float64Array(Math.max(1, d.autoDelayRttWindow));
    this.rttScratch = new Float64Array(this.rttRing.length);
    this.waitEvery = Math.max(1, Math.floor(opts.timeSyncWaitEvery ?? d.timeSyncWaitEvery));
    this.pingIntervalMs = opts.pingIntervalMs ?? d.pingIntervalMs;
    this.pumpIntervalMs = opts.pumpIntervalMs ?? d.pumpIntervalMs;
    this.disconnectTimeoutMs = opts.disconnectTimeoutMs ?? d.disconnectTimeoutMs;
    this.handshakeTimeoutMs = Math.max(
      this.disconnectTimeoutMs,
      opts.handshakeTimeoutMs ?? d.handshakeTimeoutMs,
    );
    this.detailTimeoutMs = opts.desyncDetailTimeoutMs ?? d.desyncDetailTimeoutMs;

    // States (all allocation happens here, never per tick).
    this.cur = this.game.createState(opts.config);
    this.base = this.game.tickOf(this.cur);
    // Match identity = matchId + the initial state (seed/config): mismatching peers never start.
    this.matchKey = matchKeyFor(opts.matchId, this.game.hash(this.cur));
    this.helloBuf = encodePacket({
      type: PacketType.HELLO,
      protocol: PROTOCOL_VERSION,
      playerIndex: this.localPlayer,
      matchKey: this.matchKey,
      contentHash: this.contentHash,
    });
    this.localTick = this.base;
    const n = this.maxRollback + 3;
    this.ring = [];
    for (let i = 0; i < n; i++) this.ring.push(this.game.clone(this.cur));
    this.ringTick = new Float64Array(n).fill(-1);
    this.initialPrev = this.game.clone(this.cur);
    const snapCount = Math.max(
      2,
      Math.ceil((opts.desyncSnapshotTicks ?? d.desyncSnapshotTicks) / this.hashInterval) + 1,
    );
    this.snaps = [];
    for (let i = 0; i < snapCount; i++)
      this.snaps.push({ tick: -1, hash: 0, state: this.game.clone(this.cur) });

    // Inputs: ticks [base, base + delay) are neutral.
    const neutral = sanitizeInput(this.game.neutralInput);
    this.neutral = neutral;
    this.lastLocal = neutral;
    this.localEnd = this.base;
    for (let i = 0; i < this.delay; i++) this.localIn[this.localEnd++ & INPUT_MASK_RING] = neutral;
    this.remoteEnd = this.base;
    this.ackedEnd = this.base;
    this.confirmedEnd = this.base;
    this.stepInputs = [neutral, neutral];
    this.confirmedInputs = [neutral, neutral];
    for (let c = 0; c <= MAX_INPUTS_PER_PACKET; c++)
      this.inViews.push(this.inBuf.subarray(0, INPUT_HEADER_BYTES + c));

    const userKey = opts.eventKey;
    this.keyOf = userKey ? (e) => userKey(e) : (e, tick) => defaultEventKey(e, tick);
    this.sink = { push: (e: E) => this.onEvent(e) };
    this.confirmedOnly = opts.confirmedOnly ?? null;

    this.net = {
      rttMs: 0,
      jitterMs: 0,
      inputDelay: this.delay,
      lastRollback: 0,
      maxRollback: 0,
      frameAdvantage: 0,
      remoteInputDelay: 0,
      stalls: 0,
      transport: this.transport.kind,
      confirmedTick: -1,
    };

    // Subscribe right away: an early HELLO/PING from a peer that started first is not lost.
    this.unsubs.push(
      this.transport.onMessage((data) => this.onPacket(data)),
      this.transport.onStateChange((s) => {
        if (s === 'closed') this.disconnect('transport_closed');
      }),
    );
  }

  // ── MatchDriver surface ─────────────────────────────────────────────────────────────────────────────

  get status(): DriverStatus {
    return this._status;
  }
  get current(): S {
    return this.cur;
  }
  get previous(): S {
    if (this.localTick <= this.base) return this.initialPrev;
    const t = this.localTick - 1;
    const slot = t % this.ring.length;
    return this.ringTick[slot] === t ? this.ring[slot]! : this.initialPrev;
  }
  get inputDelay(): number {
    return this.delay;
  }
  get disconnectReason(): DisconnectReason | null {
    return this._disconnectReason;
  }
  get desyncReport(): DesyncReport | null {
    return this._desyncReport;
  }
  get lastRefusal(): TickRefusal | null {
    return this._lastRefusal;
  }

  start(): void {
    if (this.started || this.disposed) return;
    this.started = true;
    this.startedAt = this.scheduler.now();
    this.lastRecvAt = this.startedAt;
    if (this.transport.state === 'closed') {
      this.disconnect('transport_closed');
      return;
    }
    if (!this.helloOk) this.sendHello();
    else this.setStatus('running');
    this.sendPing(this.startedAt);
    this.cancelPump = this.scheduler.every(this.pumpIntervalMs, () => this.pump());
  }

  tick(localInputs: readonly InputFrame[]): boolean {
    if (this.disposed || !this.started) return this.refuse(this.disposed ? 'over' : 'waiting');
    const now = this.scheduler.now();
    this.checkLiveness(now);
    if (!this.helloOk) return this.refuse(TERMINAL.has(this._status) ? 'over' : 'waiting');
    if (TERMINAL.has(this._status)) {
      this.flushFinalInputs(now);
      return this.refuse('over');
    }
    const raw = localInputs.length > 1 ? localInputs[this.localPlayer] : localInputs[0];
    const sample = sanitizeInput(raw ?? 0);

    this.applyRollback();
    this.updateConfirmed();
    if (TERMINAL.has(this._status)) return this.refuse('over');

    this.sampleAdvantage();
    // Prediction barrier: simulating tick L leaves L + 1 − remoteEnd ticks predicted.
    if (!this.canPredict()) {
      this.m.stallTicks++;
      // Single stall ticks at the window's edge are normal; report 'stalled' (UI: "waiting for opponent") only
      // when it lasts.
      if (++this.consecutiveStalls >= STALL_STATUS_TICKS) this.setStatus('stalled');
      this.tapCarry |= sample & TAP_CARRY_BITS;
      // The client loop consumed this tick's time: owe it, repaid once the peer is back and we are behind.
      if (this.owedTicks < this.maxRepay) this.owedTicks++;
      this.owedUntilTick = this.localTick + NETCODE_DEFAULTS.stallRepayWindowTicks;
      // Keep acks + our senderTick fresh for the peer (it may be waiting on exactly that).
      this.sendInputs(now);
      return this.refuse('stall');
    }
    if (this.shouldWait()) {
      this.tapCarry |= sample & TAP_CARRY_BITS;
      this.consecutiveStalls = 0;
      this.setStatus('running');
      this.sendInputs(now);
      return this.refuse('wait');
    }
    this.consecutiveStalls = 0;
    this.setStatus('running');
    const carried = sample | this.tapCarry;
    this.tapCarry = 0;
    this.addLocalInput(carried);
    this.simulate(this.localTick);
    this.ticksSinceWait++;
    this.repayOwedTick(sample);
    this.sendInputs(now);
    this.updateConfirmed();
    this._lastRefusal = null;
    return true;
  }

  private refuse(reason: TickRefusal): false {
    this._lastRefusal = reason;
    return false;
  }

  /** Prediction barrier: may we simulate tick `localTick` (≤ maxRollback ticks past the last remote input)? */
  private canPredict(): boolean {
    return this.localTick + 1 - this.remoteEnd <= this.maxRollback;
  }

  /**
   * Hitch recovery: ticks refused at the prediction barrier were real time the client loop consumed. When the
   * peer is back and time sync says we are behind, run one extra tick (holding the same sample) per accepted
   * call until they are repaid — instead of the peer burning ≤ 1 wait frame per 30 ticks for seconds while it
   * predicts us 7–10 ticks deep.
   */
  private repayOwedTick(sample: InputFrame): void {
    if (this.owedTicks <= 0) return;
    if (this.localTick > this.owedUntilTick) {
      this.owedTicks = 0;
      return;
    }
    if (this.advCount < ADV_WINDOW / 2 || this.avgAdvantage() > -0.5 || !this.canPredict()) return;
    this.owedTicks--;
    this.addLocalInput(sample);
    this.simulate(this.localTick);
    this.ticksSinceWait++;
    this.m.repaidTicks++;
    // We moved one tick forward relative to the peer: age the advantage window like a wait frame (mirrored).
    for (let i = 0; i < ADV_WINDOW; i++) this.advSamples[i] = this.advSamples[i]! + 1;
    this.advSum += this.advCount;
  }

  drainEvents(out: E[]): void {
    for (let i = 0; i < this.pending.length; i++) out.push(this.pending[i]!);
    this.pending.length = 0;
  }

  onConfirmed(handler: (tick: number, inputs: readonly InputFrame[]) => void): () => void {
    this.confirmedHandlers = [...this.confirmedHandlers, handler];
    return () => {
      this.confirmedHandlers = this.confirmedHandlers.filter((h) => h !== handler);
    };
  }

  onStatusChange(handler: (status: DriverStatus) => void): () => void {
    this.statusHandlers = [...this.statusHandlers, handler];
    return () => {
      this.statusHandlers = this.statusHandlers.filter((h) => h !== handler);
    };
  }

  netStats(): NetStats {
    const n = this.net;
    n.rttMs = this.transport.stats.rttMs;
    n.jitterMs = this.transport.stats.rttVarMs;
    n.inputDelay = this.delay;
    n.lastRollback = this.m.lastRollbackDepth;
    n.maxRollback = this.m.maxRollbackDepth;
    n.frameAdvantage = this.avgAdvantage();
    n.remoteInputDelay = this.remoteDelay;
    n.stalls = this.m.stallTicks;
    n.transport = this.transport.kind;
    n.confirmedTick = this.confirmedEnd - 1;
    return n;
  }

  metrics(): SessionMetrics {
    const m = this.m;
    return {
      ticks: this.localTick - this.base,
      confirmedTick: this.confirmedEnd - 1,
      rollbacks: m.rollbacks,
      resimulatedTicks: m.resimulatedTicks,
      simulatedTicks: m.simulatedTicks,
      lastRollbackDepth: m.lastRollbackDepth,
      maxRollbackDepth: m.maxRollbackDepth,
      avgRollbackDepth: m.rollbacks > 0 ? m.resimulatedTicks / m.rollbacks : 0,
      stallTicks: m.stallTicks,
      waitFrames: m.waitFrames,
      repaidTicks: m.repaidTicks,
      frameAdvantage: this.avgAdvantage(),
      inputDelay: this.delay,
      autoDelayRttMs: this.rttEst,
      remoteInputDelay: this.remoteDelay,
      delayChanges: m.delayChanges,
      repeatedInputs: m.repeatedInputs,
      skippedInputs: m.skippedInputs,
      hashesSent: m.hashesSent,
      hashResends: m.hashResends,
      hashesCompared: m.hashesCompared,
      hashesMatched: m.hashesMatched,
      lastVerifiedTick: m.lastVerifiedTick,
      packetsMalformed: m.packetsMalformed,
      packetsRejected: m.packetsRejected,
      duplicateInputs: m.duplicateInputs,
      eventsDelivered: m.eventsDelivered,
      eventsDeduplicated: m.eventsDeduplicated,
      rttMs: this.transport.stats.rttMs,
      jitterMs: this.transport.stats.rttVarMs,
      msSinceLastPacket: this.scheduler.now() - this.lastRecvAt,
    };
  }

  setInputDelay(ticks: number): void {
    this.autoDelay = false;
    this.applyDelay(ticks);
  }

  leave(reason: QuitReason = QuitReason.QUIT): void {
    if (this.disposed) return;
    // The match already ended at a confirmed game-over tick: there is nothing to quit, and a QUIT could reach a
    // peer still waiting for our last inputs and turn its result into 'quit'. The final inputs keep flowing.
    if (this.gameOverTick >= 0 && this._status === 'ended') return;
    const pkt = encodePacket({ type: PacketType.QUIT, reason });
    for (let i = 0; i < QUIT_COPIES; i++) this.send(pkt);
    if (!TERMINAL.has(this._status)) {
      this._disconnectReason = 'left';
      this.setStatus('ended');
    }
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    if (!TERMINAL.has(this._status)) this.setStatus('ended');
    this.confirmedHandlers = [];
    this.statusHandlers = [];
    this.pending.length = 0;
    this.heldEv.length = 0;
    this.heldTick.length = 0;
    this.heldHead = 0;
    if (this.needsFinalFlush()) {
      // The client disposes in the frame the match ended; the peer may still be missing our last inputs (a
      // single lost packet at input delay 0). Linger silently until it acked them all, or time out.
      const now = this.scheduler.now();
      this.lingerUntil = now + this.disconnectTimeoutMs;
      this.sendInputs(now);
      return;
    }
    this.teardown();
  }

  /** Game over confirmed, but the peer has not acked every input it needs to confirm the same tick. */
  private needsFinalFlush(): boolean {
    return (
      this.gameOverTick >= 0 &&
      this._status === 'ended' &&
      this.ackedEnd < this.gameOverTick &&
      this.cancelPump !== null &&
      this.transport.state !== 'closed'
    );
  }

  private flushFinalInputs(now: number): void {
    if (this.needsFinalFlush()) this.sendInputs(now);
  }

  private teardown(): void {
    if (this.closed) return;
    this.closed = true;
    this.cancelPump?.();
    this.cancelPump = null;
    for (const u of this.unsubs) u();
    this.unsubs.length = 0;
  }

  /** Pump while lingering after dispose(): final inputs + hash only, until acked or timed out. */
  private lingerPump(): void {
    const now = this.scheduler.now();
    if (!this.needsFinalFlush() || now >= this.lingerUntil) {
      this.teardown();
      return;
    }
    this.sendInputs(now);
    if (this.hashResendsLeft > 0 && this.lastLocalHashTick >= 0) {
      this.hashResendsLeft--;
      this.sendHash(this.lastLocalHashTick, this.lastLocalHash, false);
      this.m.hashResends++;
    }
  }

  /** Packets while lingering: only acks matter; the peer's next session (foreign HELLO) or a QUIT ends it. */
  private onLingerPacket(data: Uint8Array): void {
    const p = decodePacket(data);
    if (!p) return;
    if (p.type === PacketType.INPUT) {
      if (p.ackTick !== NO_TICK) {
        const acked = p.ackTick + 1;
        if (acked <= this.localEnd && acked > this.ackedEnd) this.ackedEnd = acked;
      }
      if (!this.needsFinalFlush()) this.teardown();
    } else if (p.type === PacketType.QUIT || (p.type === PacketType.HELLO && p.matchKey !== this.matchKey)) {
      this.teardown();
    }
  }

  // ── Simulation ──────────────────────────────────────────────────────────────────────────────────────

  private predictRemote(): InputFrame {
    return this.remoteEnd > this.base ? this.remoteIn[(this.remoteEnd - 1) & INPUT_MASK_RING]! : this.neutral;
  }

  /** Simulate tick `t` (== localTick) from `cur`, saving state t into the ring first. */
  private simulate(t: number): void {
    const slot = t % this.ring.length;
    this.game.cloneInto(this.ring[slot]!, this.cur);
    this.ringTick[slot] = t;
    const i = t & INPUT_MASK_RING;
    const r = this.remoteTicks[i] === t ? this.remoteIn[i]! : this.predictRemote();
    this.usedRemote[i] = r;
    this.stepInputs[this.localPlayer] = this.localIn[i]!;
    this.stepInputs[this.remotePlayer] = r;
    this.steppingTick = t;
    this.game.step(this.cur, this.stepInputs, this.sink);
    this.localTick = t + 1;
    this.m.simulatedTicks++;
  }

  private applyRollback(): void {
    const t0 = this.firstIncorrect;
    if (t0 < 0) return;
    this.firstIncorrect = -1;
    if (t0 >= this.localTick) return;
    const slot = t0 % this.ring.length;
    if (this.ringTick[slot] !== t0) {
      // Unreachable by construction (the barrier keeps every unconfirmed tick inside the ring).
      this.logger.error(
        `[netcode] rollback target ${t0} is outside the snapshot ring (tick ${this.localTick})`,
      );
      this.disconnect('error');
      return;
    }
    const target = this.localTick;
    this.discardHeldFrom(t0);
    this.game.cloneInto(this.cur, this.ring[slot]!);
    for (let t = t0; t < target; t++) this.simulate(t);
    const depth = target - t0;
    const m = this.m;
    m.rollbacks++;
    m.resimulatedTicks += depth;
    m.lastRollbackDepth = depth;
    if (depth > m.maxRollbackDepth) m.maxRollbackDepth = depth;
  }

  private addLocalInput(sample: InputFrame): void {
    const target = this.localTick + this.delay;
    if (this.localEnd > target) {
      // Delay decreased: this sample's tick was already sent; drop it (its taps carry to the next sample).
      this.m.skippedInputs++;
      this.tapCarry |= sample & TAP_CARRY_BITS;
      return;
    }
    while (this.localEnd < target) {
      // Delay increased: repeat the last input to keep ticks contiguous.
      this.localIn[this.localEnd++ & INPUT_MASK_RING] = this.lastLocal;
      this.m.repeatedInputs++;
    }
    this.localIn[this.localEnd++ & INPUT_MASK_RING] = sample;
    this.lastLocal = sample;
  }

  private applyDelay(ticks: number): void {
    const next = clamp(Math.round(ticks), this.minDelay, this.maxDelay);
    if (next === this.delay) return;
    this.delay = next;
    this.m.delayChanges++;
  }

  private onEvent(e: E): void {
    this.opts.debugEvents?.push(e);
    if (this.confirmedOnly?.(e)) {
      // Held until its tick is confirmed; a rollback past it discards and re-collects it.
      if (this.heldEv.length - this.heldHead < MAX_HELD_EVENTS) {
        this.heldEv.push(e);
        this.heldTick.push(this.steppingTick);
      }
      return;
    }
    const key = this.keyOf(e, this.steppingTick);
    if (this.delivered.has(key)) {
      this.m.eventsDeduplicated++;
      return;
    }
    this.delivered.add(key);
    this.deliveredLog.push({ tick: this.steppingTick, key });
    if (this.pending.length >= MAX_PENDING_EVENTS) this.pending.shift();
    this.pending.push(e);
    this.m.eventsDelivered++;
  }

  /** A rollback to `t0` resimulates every tick ≥ t0: their held confirmed-only events are re-collected. */
  private discardHeldFrom(t0: number): void {
    const ev = this.heldEv;
    const tk = this.heldTick;
    let n = ev.length;
    while (n > this.heldHead && tk[n - 1]! >= t0) n--;
    ev.length = n;
    tk.length = n;
  }

  /** Deliver the held confirmed-only events of every tick < confirmedEnd (in order). */
  private releaseHeld(): void {
    const ev = this.heldEv;
    const tk = this.heldTick;
    let h = this.heldHead;
    while (h < ev.length && tk[h]! < this.confirmedEnd) {
      if (this.pending.length >= MAX_PENDING_EVENTS) this.pending.shift();
      this.pending.push(ev[h]!);
      this.m.eventsDelivered++;
      h++;
    }
    if (h === ev.length) {
      ev.length = 0;
      tk.length = 0;
      h = 0;
    } else if (h >= 256) {
      ev.splice(0, h);
      tk.splice(0, h);
      h = 0;
    }
    this.heldHead = h;
  }

  // ── Confirmation, hashes, game over ─────────────────────────────────────────────────────────────────

  private stateAt(t: number): S | null {
    if (t === this.localTick) return this.cur;
    const slot = t % this.ring.length;
    return this.ringTick[slot] === t ? this.ring[slot]! : null;
  }

  private updateConfirmed(): void {
    // The confirmed timeline stops at the game-over state (both peers stop at the same tick).
    if (this.gameOverTick >= 0) return;
    const end = Math.min(this.remoteEnd, this.localEnd, this.localTick);
    if (end <= this.confirmedEnd || this.firstIncorrect >= 0) return;
    const isGameOver = this.opts.isGameOver;
    while (this.confirmedEnd < end) {
      const t = this.confirmedEnd;
      const i = t & INPUT_MASK_RING;
      this.confirmedInputs[this.localPlayer] = this.localIn[i]!;
      this.confirmedInputs[this.remotePlayer] = this.remoteIn[i]!;
      const handlers = this.confirmedHandlers;
      for (let k = 0; k < handlers.length; k++) handlers[k]!(t, this.confirmedInputs);
      this.confirmedEnd = t + 1;
      if (this.confirmedEnd % this.hashInterval === 0) this.recordHash(this.confirmedEnd);
      if (isGameOver) {
        const s = this.stateAt(this.confirmedEnd);
        if (s && isGameOver(s)) {
          this.gameOverTick = this.confirmedEnd;
          // Hash the final state too (if not on the interval) so the result is verified by both peers.
          if (this.confirmedEnd % this.hashInterval !== 0) this.recordHash(this.confirmedEnd);
          // Flush our last inputs right away: the peer needs every input < gameOverTick to end at the same tick.
          this.sendInputs(this.scheduler.now());
          // Release the final confirmed-only events before the status handlers run (they may dispose).
          this.releaseHeld();
          if (!TERMINAL.has(this._status)) this.setStatus('ended');
          break;
        }
      }
    }
    this.releaseHeld();
    // Ticks below confirmedEnd are never resimulated again: forget their event keys.
    const log = this.deliveredLog;
    let k = 0;
    while (k < log.length && log[k]!.tick < this.confirmedEnd) this.delivered.delete(log[k++]!.key);
    if (k > 0) log.splice(0, k);
  }

  private recordHash(t: number): void {
    const s = this.stateAt(t);
    if (!s) return;
    const hash = this.game.hash(s) >>> 0;
    const snap = this.snaps[Math.floor(t / this.hashInterval) % this.snaps.length]!;
    this.game.cloneInto(snap.state, s);
    snap.tick = t;
    snap.hash = hash;
    this.lastLocalHashTick = t;
    this.lastLocalHash = hash;
    this.hashResendsLeft = this.hashResendCount;
    this.sendHash(t, hash);
    this.compareHash(t);
  }

  private findSnap(t: number): HashSnap<S> | null {
    const snap = this.snaps[Math.floor(t / this.hashInterval) % this.snaps.length]!;
    return snap.tick === t ? snap : null;
  }

  private remoteHashSlot(t: number): number {
    return Math.floor(t / this.hashInterval) % REMOTE_HASH_SLOTS;
  }

  private remoteHashOf(t: number): number | null {
    const k = this.remoteHashSlot(t);
    return this.remoteHashTick[k] === t ? this.remoteHashVal[k]! : null;
  }

  private compareHash(t: number): void {
    const snap = this.findSnap(t);
    const remote = this.remoteHashOf(t);
    if (!snap || remote === null) return;
    this.m.hashesCompared++;
    if (snap.hash === remote) {
      this.m.hashesMatched++;
      if (t > this.m.lastVerifiedTick) this.m.lastVerifiedTick = t;
      return;
    }
    this.enterDesync(t, snap.hash, remote, null);
  }

  private partsOf(state: S): number[] {
    const out = this.partsScratch;
    out.fill(0);
    if (this.opts.hashParts) this.opts.hashParts(state, out);
    else out[0] = this.game.hash(state) >>> 0;
    return Array.from(out, (v) => v >>> 0);
  }

  private enterDesync(
    t: number,
    localHash: number,
    remoteHash: number | null,
    remoteParts: number[] | null,
  ): void {
    if (this.desync) return;
    const snap = this.findSnap(t);
    let snapshotJson: string | null = null;
    if (snap) {
      try {
        snapshotJson = JSON.stringify(snap.state);
      } catch (err) {
        snapshotJson = `<unserializable snapshot: ${String(err)}>`;
      }
    }
    this.desync = {
      tick: t,
      localHash,
      remoteHash,
      localParts: snap ? this.partsOf(snap.state) : null,
      remoteParts,
      snapshotJson,
      deadline: this.scheduler.now() + this.detailTimeoutMs,
      finalized: false,
    };
    this.setStatus('desynced');
    this.sendHashDetail();
    if (remoteParts) this.finalizeDesync();
  }

  private finalizeDesync(): void {
    const d = this.desync;
    if (!d || d.finalized) return;
    d.finalized = true;
    const divergent: string[] = [];
    if (d.localParts && d.remoteParts) {
      for (let i = 0; i < HASH_PART_COUNT; i++) {
        if (d.localParts[i] !== d.remoteParts[i]) divergent.push(HASH_PART_NAMES[i]!);
      }
    }
    const report: DesyncReport = {
      tick: d.tick,
      localHash: d.localHash,
      remoteHash: d.remoteHash,
      localParts: d.localParts,
      remoteParts: d.remoteParts,
      divergentParts: divergent,
      snapshotJson: d.snapshotJson,
    };
    this._desyncReport = report;
    const remoteStr = d.remoteHash === null ? 'unknown' : formatHash(d.remoteHash);
    this.logger.error(
      `[netcode] DESYNC at confirmed tick ${d.tick} (player ${this.localPlayer}): local ${formatHash(d.localHash)} ` +
        `remote ${remoteStr}; divergent sections: ${divergent.length ? divergent.join(', ') : d.remoteParts ? 'none?' : 'unknown (no HASH_DETAIL)'}`,
    );
    this.logger.error(`[netcode] snapshot of tick ${d.tick} (player ${this.localPlayer}):`, d.snapshotJson);
    // Make sure the peer stops too, even if it never saw the mismatch itself (its copy of our HASH was lost and
    // it no longer keeps the snapshot to compare our HASH_DETAIL with).
    const quit = encodePacket({ type: PacketType.QUIT, reason: QuitReason.DESYNC });
    for (let i = 0; i < QUIT_COPIES; i++) this.send(quit);
    this.opts.onDesyncReport?.(report);
    this.opts.onDesync?.(d.tick, d.localHash, d.remoteHash ?? 0);
  }

  // ── Time sync & auto delay ──────────────────────────────────────────────────────────────────────────

  private localAdvantage(): number {
    if (!this.haveRemoteTick) return 0;
    const halfRttTicks = this.transport.stats.rttMs / 2 / TICK_MS;
    return this.localTick - (this.remoteSenderTick + halfRttTicks);
  }

  private sampleAdvantage(): void {
    if (!this.haveRemoteTick) return;
    const v = (this.localAdvantage() - this.remoteAdv) / 2;
    if (this.advCount === ADV_WINDOW) this.advSum -= this.advSamples[this.advIdx]!;
    else this.advCount++;
    this.advSamples[this.advIdx] = v;
    this.advSum += v;
    this.advIdx = (this.advIdx + 1) % ADV_WINDOW;
  }

  private avgAdvantage(): number {
    return this.advCount > 0 ? this.advSum / this.advCount : 0;
  }

  private shouldWait(): boolean {
    if (this.ticksSinceWait < this.waitEvery || this.advCount < ADV_WINDOW / 2) return false;
    if (this.avgAdvantage() < WAIT_THRESHOLD) return false;
    this.ticksSinceWait = 0;
    this.m.waitFrames++;
    // The wait shifts our timeline by one tick: age the window so we don't double-count it.
    for (let i = 0; i < ADV_WINDOW; i++) this.advSamples[i] = this.advSamples[i]! - 1;
    this.advSum -= this.advCount;
    return true;
  }

  /** Record a PONG-measured RTT sample in the percentile window. */
  private addRttSample(ms: number): void {
    const ring = this.rttRing;
    ring[this.rttRingIdx] = ms;
    this.rttRingIdx = (this.rttRingIdx + 1) % ring.length;
    if (this.rttRingCount < ring.length) this.rttRingCount++;
  }

  /** `autoDelayRttPercentile` of the RTT window (sorted copy in a preallocated scratch: no allocation). */
  private rttPercentile(): number {
    const n = this.rttRingCount;
    if (n === 0) return 0;
    const sc = this.rttScratch;
    for (let i = 0; i < n; i++) sc[i] = this.rttRing[i]!;
    for (let i = n; i < sc.length; i++) sc[i] = Infinity;
    sc.sort();
    return sc[Math.floor(NETCODE_DEFAULTS.autoDelayRttPercentile * (n - 1))]!;
  }

  /**
   * Auto input delay (see NETCODE_DEFAULTS.autoDelayRollbackBudget): robust latency estimate (RTT median), a dead
   * band around the current delay and a hold time before every change, so a stable link never flaps.
   */
  private updateAutoDelay(now: number): void {
    if (!this.autoDelay) return;
    const d = NETCODE_DEFAULTS;
    if (this.rttRingCount < Math.min(d.autoDelayMinSamples, this.rttRing.length)) return;
    if (now - this.lastAutoDelayAt < d.autoDelayIntervalMs) return;
    this.lastAutoDelayAt = now;
    this.rttEst = this.rttPercentile();
    const x = this.rttEst / 2 / TICK_MS - d.autoDelayRollbackBudget;
    const lo = this.minAutoDelay;
    if (!this.autoDelayInitialized) {
      this.autoDelayInitialized = true;
      this.applyDelay(clamp(Math.ceil(x - AUTO_DELAY_BAND_UP), lo, this.maxDelay));
      return;
    }
    if (x > this.delay + AUTO_DELAY_BAND_UP && this.delay < this.maxDelay) {
      this.autoDownSince = -Infinity;
      if (this.autoUpSince === -Infinity) this.autoUpSince = now;
      if (now - this.autoUpSince >= d.autoDelayUpHoldMs) {
        this.applyDelay(this.delay + 1);
        this.autoUpSince = -Infinity;
      }
    } else if (x < this.delay - AUTO_DELAY_BAND_DOWN && this.delay > lo) {
      this.autoUpSince = -Infinity;
      if (this.autoDownSince === -Infinity) this.autoDownSince = now;
      if (now - this.autoDownSince >= d.autoDelayDownHoldMs) {
        this.applyDelay(this.delay - 1);
        this.autoDownSince = -Infinity;
      }
    } else {
      this.autoUpSince = -Infinity;
      this.autoDownSince = -Infinity;
    }
  }

  // ── Connection lifecycle ────────────────────────────────────────────────────────────────────────────

  private setStatus(s: DriverStatus): void {
    if (this._status === s) return;
    this._status = s;
    const handlers = this.statusHandlers;
    for (let k = 0; k < handlers.length; k++) handlers[k]!(s);
  }

  private disconnect(reason: DisconnectReason): void {
    if (TERMINAL.has(this._status)) return;
    this._disconnectReason = reason;
    this.setStatus('disconnected');
  }

  private checkLiveness(now: number): void {
    if (!this.started || TERMINAL.has(this._status)) return;
    if (this.transport.state === 'closed') {
      this.disconnect('transport_closed');
      return;
    }
    const limit = this.helloOk ? this.disconnectTimeoutMs : this.handshakeTimeoutMs;
    if (now - this.lastRecvAt > limit) this.disconnect('timeout');
  }

  private pump(): void {
    if (this.closed) return;
    if (this.disposed) {
      this.lingerPump();
      return;
    }
    const now = this.scheduler.now();
    this.checkLiveness(now);
    const st = this._status;
    if (st === 'disconnected') return;
    if (!this.helloOk) {
      this.sendHello();
    } else {
      // Our answer to the peer's HELLO may have been lost while its re-sent HELLOs stopped reaching us (a path that
      // turned one-way at kickoff, a starved tab). Until its first INPUT proves it has our HELLO, keep offering it
      // unprompted instead of only answering — the peer's handshake can never hang on one lost packet.
      if (
        !this.peerReady &&
        st !== 'desynced' &&
        now - this.lastHelloAnswerAt >= this.pumpIntervalMs * 0.75
      ) {
        this.lastHelloAnswerAt = now;
        this.sendHello();
      }
      // Inputs that arrived while tick() was not called (hidden tab, end of match): correct the timeline and
      // confirm, so hashes, onConfirmed and the game-over check keep flowing.
      if (st === 'running' || st === 'stalled' || st === 'ended') {
        this.applyRollback();
        this.updateConfirmed();
      }
      // Keep inputs/acks flowing even when tick() is not called (hidden tab) or after the match ended.
      if (now - this.lastInputSentAt >= this.pumpIntervalMs * 0.75) this.sendInputs(now);
      // A new hash is re-sent by the next few pumps only (an unreliable channel may drop one copy); the relay is
      // reliable, so it gets none.
      if (this.hashResendsLeft > 0 && this.lastLocalHashTick >= 0) {
        this.hashResendsLeft--;
        this.sendHash(this.lastLocalHashTick, this.lastLocalHash, false);
        this.m.hashResends++;
      }
    }
    if (now - this.lastPingAt >= this.pingIntervalMs) this.sendPing(now);
    const d = this.desync;
    if (d && !d.finalized) {
      this.sendHashDetail();
      if (now >= d.deadline) this.finalizeDesync();
    }
    if (st !== 'desynced' && st !== 'ended') this.updateAutoDelay(now);
  }

  // ── Sending ─────────────────────────────────────────────────────────────────────────────────────────

  private send(data: Uint8Array): void {
    if (this.transport.state === 'closed') return;
    this.transport.send(data);
  }

  private sendHello(): void {
    this.send(this.helloBuf);
  }

  /** PING/PONG share one buffer (layout: type u8 · id u32 · timeMs f64). */
  private sendPingPong(
    type: typeof PacketType.PING | typeof PacketType.PONG,
    id: number,
    timeMs: number,
  ): void {
    const dv = this.pingView;
    dv.setUint8(0, type);
    dv.setUint32(1, id >>> 0, true);
    dv.setFloat64(5, timeMs, true);
    this.send(this.pingBuf);
  }

  private sendPing(now: number): void {
    this.lastPingAt = now;
    this.pingId = (this.pingId + 1) >>> 0;
    this.sendPingPong(PacketType.PING, this.pingId, now);
  }

  /** Encode INPUT into the reusable buffer (layout: @hf/shared packet.ts) and send it. */
  private sendInputs(now: number): void {
    if (!this.helloOk || this.closed) return;
    const start = this.ackedEnd;
    const count = Math.min(this.localEnd - start, MAX_INPUTS_PER_PACKET);
    if (this.localEnd - start > INPUT_RING - MAX_REMOTE_LEAD) {
      this.logger.error('[netcode] peer stopped acknowledging inputs; input window overflow');
      this.disconnect('error');
      return;
    }
    const dv = this.inView;
    dv.setUint8(0, PacketType.INPUT);
    dv.setUint32(1, this.remoteEnd > this.base ? (this.remoteEnd - 1) >>> 0 : NO_TICK, true);
    dv.setUint32(5, start >>> 0, true);
    dv.setUint8(9, count);
    dv.setInt8(10, clamp(Math.round(this.localAdvantage()), -128, 127));
    dv.setUint32(11, this.localTick >>> 0, true);
    dv.setUint8(15, this.delay);
    for (let c = 0; c < count; c++)
      this.inBuf[INPUT_HEADER_BYTES + c] = this.localIn[(start + c) & INPUT_MASK_RING]!;
    this.send(this.inViews[count]!);
    this.lastInputSentAt = now;
  }

  private sendHash(tick: number, hash: number, count = true): void {
    const dv = this.hashView;
    dv.setUint8(0, PacketType.HASH);
    dv.setUint32(1, tick >>> 0, true);
    dv.setUint32(5, hash >>> 0, true);
    this.send(this.hashBuf);
    if (count) this.m.hashesSent++;
  }

  private sendHashDetail(): void {
    const d = this.desync;
    if (!d?.localParts) return;
    this.send(encodePacket({ type: PacketType.HASH_DETAIL, tick: d.tick, parts: d.localParts }));
  }

  // ── Receiving ───────────────────────────────────────────────────────────────────────────────────────

  private onPacket(data: Uint8Array): void {
    if (this.closed) return;
    if (this.disposed) {
      this.onLingerPacket(data);
      return;
    }
    const p: GamePacket | null = decodePacket(data);
    if (!p) {
      this.m.packetsMalformed++;
      return;
    }
    const now = this.scheduler.now();
    switch (p.type) {
      case PacketType.HELLO:
        this.onHello(p, now);
        return;
      case PacketType.PING:
        this.lastRecvAt = now;
        this.sendPingPong(PacketType.PONG, p.id, p.timeMs);
        return;
      case PacketType.PONG: {
        const sample = now - p.timeMs;
        if (!(sample >= 0) || sample > 60_000) {
          this.m.packetsRejected++;
          return;
        }
        this.lastRecvAt = now;
        this.transport.reportRtt(sample);
        this.addRttSample(sample);
        return;
      }
      case PacketType.QUIT:
        this.onQuit(p.reason);
        return;
      default:
        break;
    }
    if (!this.helloOk) {
      // Inputs/hashes before the version handshake are not trusted; the peer re-sends them.
      this.m.packetsRejected++;
      return;
    }
    switch (p.type) {
      case PacketType.INPUT:
        this.onInput(p, now);
        return;
      case PacketType.HASH:
        this.onHash(p.tick, p.hash, now);
        return;
      case PacketType.HASH_DETAIL:
        this.onHashDetail(p, now);
        return;
    }
  }

  private onHello(p: HelloPacket, now: number): void {
    const mismatch =
      p.protocol !== PROTOCOL_VERSION || p.matchKey !== this.matchKey || p.contentHash !== this.contentHash;
    if (mismatch && (this.helloOk || TERMINAL.has(this._status))) {
      // Our match is already established (or over): this is the peer's NEXT session on the same transport (a
      // rematch with a fresh seed) while ours is not disposed yet. Stay silent; never QUIT it.
      this.m.packetsRejected++;
      return;
    }
    if (mismatch) {
      this.logger.warn(
        `[netcode] HELLO mismatch: protocol ${p.protocol}/${PROTOCOL_VERSION}, matchKey ${formatHash(p.matchKey)}/` +
          `${formatHash(this.matchKey)}, content ${formatHash(p.contentHash)}/${formatHash(this.contentHash)}`,
      );
      const quit = encodePacket({ type: PacketType.QUIT, reason: QuitReason.VERSION });
      for (let i = 0; i < QUIT_COPIES; i++) this.send(quit);
      this.disconnect('version');
      return;
    }
    if (p.playerIndex !== this.remotePlayer) {
      this.logger.warn(`[netcode] HELLO from player ${p.playerIndex}, expected ${this.remotePlayer}`);
      const quit = encodePacket({ type: PacketType.QUIT, reason: QuitReason.ERROR });
      for (let i = 0; i < QUIT_COPIES; i++) this.send(quit);
      this.disconnect('error');
      return;
    }
    this.lastRecvAt = now;
    // Answer while the peer may still lack our HELLO (it re-sends until it has it). Once its INPUT arrives it is
    // done; answers are rate-limited so two answering peers can never echo HELLOs back and forth forever.
    const firstContact = !this.helloOk;
    if (
      (firstContact || (!this.peerReady && now - this.lastHelloAnswerAt >= this.pumpIntervalMs / 2)) &&
      this._status !== 'disconnected' &&
      this._status !== 'desynced'
    ) {
      this.lastHelloAnswerAt = now;
      this.sendHello();
    }
    if (!firstContact) return;
    this.helloOk = true;
    if (this.started && !TERMINAL.has(this._status)) {
      this.setStatus('running');
      this.sendInputs(now);
    }
  }

  private onQuit(reason: QuitReason): void {
    this.lastRecvAt = this.scheduler.now();
    if (this._status === 'ended') return;
    switch (reason) {
      case QuitReason.DESYNC:
        if (!TERMINAL.has(this._status)) {
          this.logger.error(
            `[netcode] peer reported a desync (player ${this.localPlayer}, last local hash tick ${this.lastLocalHashTick})`,
          );
          this.setStatus('desynced');
        }
        return;
      case QuitReason.VERSION:
        this.disconnect('version');
        return;
      case QuitReason.ERROR:
        this.disconnect('error');
        return;
      default:
        this.disconnect('quit');
    }
  }

  private onInput(p: InputPacket, now: number): void {
    const count = p.inputs.length;
    // Plausibility: a peer can never be further ahead than our inputs + its prediction window.
    if (
      (count > 0 && p.startTick + count > this.localEnd + MAX_REMOTE_LEAD) ||
      p.senderTick > this.localEnd + MAX_REMOTE_LEAD
    ) {
      this.m.packetsRejected++;
      return;
    }
    if (p.ackTick !== NO_TICK) {
      const acked = p.ackTick + 1;
      if (acked > this.localEnd) {
        this.m.packetsRejected++;
        return;
      }
      if (acked > this.ackedEnd) this.ackedEnd = acked;
    }
    // Only a plausible INPUT proves the peer finished its handshake (a lingering old session's stale INPUT on a
    // shared transport is rejected above and must not silence our HELLO answers).
    this.peerReady = true;
    this.lastRecvAt = now;
    if (!this.haveRemoteTick || p.senderTick >= this.remoteSenderTick) {
      this.haveRemoteTick = true;
      this.remoteSenderTick = p.senderTick;
      this.remoteAdv = p.frameAdvantage;
      this.remoteDelay = p.inputDelay;
    }
    for (let c = 0; c < count; c++) {
      const t = p.startTick + c;
      if (t < this.remoteEnd) {
        this.m.duplicateInputs++;
        continue;
      }
      const i = t & INPUT_MASK_RING;
      const v = p.inputs[c]!;
      if (this.remoteTicks[i] === t) {
        this.m.duplicateInputs++;
        continue;
      }
      this.remoteTicks[i] = t;
      this.remoteIn[i] = v;
      if (t < this.localTick && this.usedRemote[i] !== v) {
        if (this.firstIncorrect < 0 || t < this.firstIncorrect) this.firstIncorrect = t;
      }
    }
    while (this.remoteTicks[this.remoteEnd & INPUT_MASK_RING] === this.remoteEnd) this.remoteEnd++;
  }

  private onHash(tick: number, hash: number, now: number): void {
    // Hashes come every hashInterval confirmed ticks, plus one for the final (game-over) tick.
    if (tick <= this.base || tick > this.localEnd + MAX_REMOTE_LEAD) {
      this.m.packetsRejected++;
      return;
    }
    this.lastRecvAt = now;
    const k = this.remoteHashSlot(tick);
    if (this.remoteHashTick[k] === tick) return; // pump re-send
    this.remoteHashTick[k] = tick;
    this.remoteHashVal[k] = hash >>> 0;
    this.compareHash(tick);
  }

  private onHashDetail(p: HashDetailPacket, now: number): void {
    this.lastRecvAt = now;
    const d = this.desync;
    if (d) {
      if (d.tick === p.tick && !d.remoteParts) {
        d.remoteParts = p.parts.slice();
        // Make sure the peer gets ours even if our earlier copies were lost.
        this.sendHashDetail();
        this.finalizeDesync();
      } else if (d.tick === p.tick) {
        this.sendHashDetail();
      }
      return;
    }
    // The peer saw a mismatch we have not (our copy of its HASH was lost): check our snapshot of that tick.
    const snap = this.findSnap(p.tick);
    if (!snap) return;
    const local = this.partsOf(snap.state);
    let differs = false;
    for (let i = 0; i < HASH_PART_COUNT; i++) if (local[i] !== p.parts[i]) differs = true;
    if (!differs) return;
    this.enterDesync(p.tick, snap.hash, this.remoteHashOf(p.tick), p.parts.slice());
  }
}

export const createRollbackSession = <S, E, C>(
  opts: RollbackSessionOptions<S, E, C>,
): RollbackSession<S, E> => new RollbackSessionImpl(opts);
